// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import {TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {clickMessageComponent} from '@app/features/messaging/commands/MessageComponentCommands';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {http} from '@app/features/platform/transport/RestTransport';
import {failureMessage} from '@app/features/platform/utils/ResponseInspection';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {I18n} from '@lingui/core';

// A button on the room TV is the bot's own message button: a message this client holds goes through the chat's click
// (its private reply and the bot's pending answer show as they would in the chat); one not loaded here takes the same
// route and body. The server checks again that the button is on that message.
export async function clickRoomStageButton(
	i18n: I18n,
	channelId: string,
	messageId: string,
	customId: string,
): Promise<void> {
	const message = Messages.getMessage(channelId, messageId);
	if (message) {
		await clickMessageComponent(i18n, message, customId);
		return;
	}
	const sessionId = GatewayConnection.sessionId;
	try {
		await http.post(Endpoints.CHANNEL_MESSAGE_COMPONENTS(channelId, messageId), {
			body: {custom_id: customId, ...(sessionId ? {session_id: sessionId} : {})},
		});
	} catch (error) {
		ToastCommands.error(failureMessage(error) ?? i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
	}
}

// A moderator's button: the server checks MUTE_MEMBERS, MOVE_MEMBERS or MANAGE_CHANNELS again.
export async function closeRoomStage(i18n: I18n, channelId: string): Promise<void> {
	try {
		await http.delete(Endpoints.CHANNEL_VOICE_ROOM_STAGE(channelId));
	} catch (error) {
		ToastCommands.error(failureMessage(error) ?? i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
	}
}
